<template>
  <div>
    <div class="container">
      <div class="box-title">
        <h4>
          <i class="fal fa-tasks"></i>
          Job Dashboard
        </h4>
      </div>

      <!-- Service Down Banner -->
      <div v-if="serviceDown" class="notification is-warning service-down-banner">
        <i class="fal fa-exclamation-triangle"></i>
        <span>ລະບົບກະເປົ໋າບໍ່ສາມາດເຊື່ອມຕໍ່ໄດ້ — ການໂຫລດຂໍ້ມູນອັດຕະໂນມັດຖືກຊະລໍຖ້າ</span>
        <vs-button flat size="small" @click="retryNow" class="ml-3">
          <i class="fal fa-redo"></i> ລອງໃໝ່
        </vs-button>
      </div>

      <!-- Stats Cards -->
      <div class="box">
        <div v-if="statsError" class="notification is-warning">
          <p>{{ statsError }}</p>
          <p v-if="statsErrorRequestId" class="error-ref">ref: {{ statsErrorRequestId }}</p>
          <vs-button flat size="small" @click="fetchStats" class="mt-2">
            <i class="fal fa-redo"></i> ລອງໃໝ່
          </vs-button>
        </div>

        <div class="stats-grid">
          <div v-for="queue in queues" :key="queue" class="stat-card">
            <div class="stat-card__header">
              <span class="stat-card__title">{{ queue === 'total' ? 'Total' : queue }}</span>
              <span :class="['stat-card__badge', `badge-${queue}`]">{{ queue }}</span>
            </div>
            <div class="stat-card__body">
              <div class="stat-item">
                <span class="stat-label">Pending</span>
                <span class="stat-value">{{ getStatValue(queue, 'pending') }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Active</span>
                <span class="stat-value stat-active">{{ getStatValue(queue, 'active') }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Scheduled</span>
                <span class="stat-value">{{ getStatValue(queue, 'scheduled') }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Retry</span>
                <span class="stat-value stat-retry">{{ getStatValue(queue, 'retry') }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Archived</span>
                <span class="stat-value stat-archived">{{ getStatValue(queue, 'archived') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Job Filters -->
      <div class="box">
        <div class="columns">
          <div class="column is-2">
            <div class="control">
              <label class="label">Queue</label>
              <div class="select is-fullwidth">
                <select v-model="filters.queue" @change="onFilterChange">
                  <option value="">ທັງໝົດ</option>
                  <option value="critical">critical</option>
                  <option value="default">default</option>
                  <option value="low">low</option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">Status</label>
              <div class="select is-fullwidth">
                <select v-model="filters.status" @change="onFilterChange">
                  <option value="">ທັງໝົດ</option>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="retry">Retry</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-3">
            <div class="control">
              <label class="label">Job Type</label>
              <input
                type="text"
                class="input"
                v-model="filters.type"
                placeholder="e.g. export:transactions"
                @keyup.enter="onFilterChange"
              >
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">&nbsp;</label>
              <vs-button @click="onFilterChange" :loading="isLoadingJobs">
                <i class="fal fa-search"></i> ຄົ້ນຫາ
              </vs-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Job List -->
      <div class="box">
        <div v-if="isLoadingJobs" class="has-text-centered py-5">
          <i class="fal fa-spinner fa-spin fa-2x"></i>
          <p class="mt-3">ກຳລັງໂຫລດ...</p>
        </div>

        <div v-else-if="jobsError" class="notification is-warning">
          <p>{{ jobsError }}</p>
          <p v-if="jobsErrorRequestId" class="error-ref">ref: {{ jobsErrorRequestId }}</p>
          <vs-button @click="fetchJobs" class="mt-3">
            <i class="fal fa-redo"></i> ລອງໃໝ່
          </vs-button>
        </div>

        <div v-else-if="jobs.length === 0" class="has-text-centered py-5">
          <i class="fal fa-tasks fa-3x has-text-grey-light"></i>
          <p class="mt-3 has-text-grey">ບໍ່ພົບ jobs</p>
        </div>

        <div v-else>
          <table class="table is-fullwidth is-hoverable is-striped">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Queue</th>
                <th>Status</th>
                <th>Created</th>
                <th>Processed</th>
                <th>Error</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in jobs" :key="job.id">
                <td><small class="has-text-grey">{{ truncate(job.id, 12) }}</small></td>
                <td><code class="job-type">{{ job.type }}</code></td>
                <td>
                  <span :class="['tag', getQueueClass(job.queue)]">{{ job.queue }}</span>
                </td>
                <td>
                  <span :class="['tag', getJobStatusClass(job.status)]">{{ job.status }}</span>
                </td>
                <td><small>{{ formatDate(job.created_at) }}</small></td>
                <td><small>{{ formatDate(job.processed_at) }}</small></td>
                <td>
                  <small v-if="job.error" class="has-text-danger" :title="job.error">
                    {{ truncate(job.error, 25) }}
                  </small>
                  <span v-else class="has-text-grey">-</span>
                </td>
                <td>
                  <div class="job-actions">
                    <vs-button flat size="small" @click="showJobDetail(job)">
                      <i class="fal fa-eye"></i>
                    </vs-button>
                    <vs-button
                      v-if="isCancellable(job.status)"
                      flat
                      size="small"
                      color="danger"
                      @click="confirmCancelJob(job)"
                    >
                      <i class="fal fa-times"></i>
                    </vs-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div v-if="jobTotalPages > 1" class="pagination-wrapper">
            <nav class="pagination" role="navigation">
              <vs-button
                @click="goToPage(jobPagination.page - 1)"
                :disabled="jobPagination.page <= 1"
                flat
              >
                <i class="fal fa-chevron-left"></i>
              </vs-button>
              <span class="pagination-info">
                ໜ້າ {{ jobPagination.page }} / {{ jobTotalPages }}
              </span>
              <vs-button
                @click="goToPage(jobPagination.page + 1)"
                :disabled="jobPagination.page >= jobTotalPages"
                flat
              >
                <i class="fal fa-chevron-right"></i>
              </vs-button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail Modal -->
    <vs-dialog v-model="showDetailModal" width="700px" prevent-close>
      <template #header>
        <h4>Job Detail</h4>
      </template>

      <div v-if="selectedJob" class="p-4">
        <div class="columns is-multiline">
          <div class="column is-6">
            <strong>ID:</strong>
            <p><code>{{ selectedJob.id }}</code></p>
          </div>
          <div class="column is-6">
            <strong>Type:</strong>
            <p><code>{{ selectedJob.type }}</code></p>
          </div>
          <div class="column is-6">
            <strong>Queue:</strong>
            <p><span :class="['tag', getQueueClass(selectedJob.queue)]">{{ selectedJob.queue }}</span></p>
          </div>
          <div class="column is-6">
            <strong>Status:</strong>
            <p><span :class="['tag', getJobStatusClass(selectedJob.status)]">{{ selectedJob.status }}</span></p>
          </div>
          <div class="column is-6">
            <strong>Created:</strong>
            <p>{{ formatDate(selectedJob.created_at) }}</p>
          </div>
          <div class="column is-6">
            <strong>Processed:</strong>
            <p>{{ formatDate(selectedJob.processed_at) || '-' }}</p>
          </div>
          <div class="column is-6">
            <strong>Completed:</strong>
            <p>{{ formatDate(selectedJob.completed_at) || '-' }}</p>
          </div>
          <div class="column is-6">
            <strong>Retry:</strong>
            <p>{{ selectedJob.retry_count || 0 }} / {{ selectedJob.max_retries || 0 }}</p>
          </div>
          <div class="column is-6" v-if="selectedJob.last_failed_at">
            <strong>Last Failed:</strong>
            <p>{{ formatDate(selectedJob.last_failed_at) }}</p>
          </div>
          <div class="column is-6" v-if="selectedJob.next_retry_at">
            <strong>Next Retry:</strong>
            <p>{{ formatDate(selectedJob.next_retry_at) }}</p>
          </div>
          <div class="column is-12" v-if="selectedJob.payload">
            <strong>Payload:</strong>
            <pre class="json-display">{{ formatJson(selectedJob.payload) }}</pre>
          </div>
          <div class="column is-12" v-if="selectedJob.result">
            <strong>Result:</strong>
            <pre class="json-display">{{ formatJson(selectedJob.result) }}</pre>
          </div>
          <div class="column is-12" v-if="selectedJob.error">
            <strong>Error:</strong>
            <div class="notification is-danger is-light">
              <p>{{ selectedJob.error }}</p>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="modal-footer">
          <vs-button
            v-if="selectedJob && isCancellable(selectedJob.status)"
            color="danger"
            flat
            @click="confirmCancelJob(selectedJob)"
          >
            <i class="fal fa-times"></i> ຍົກເລີກ Job
          </vs-button>
          <vs-button @click="showDetailModal = false">ປິດ</vs-button>
        </div>
      </template>
    </vs-dialog>

    <!-- Cancel Confirmation -->
    <vs-dialog v-model="showCancelConfirm" width="400px" prevent-close>
      <template #header>
        <h4><i class="fal fa-exclamation-triangle"></i> ຢືນຢັນການຍົກເລີກ</h4>
      </template>
      <div class="confirm-body">
        <p>ຍົກເລີກ job #{{ cancelTarget?.id }}?</p>
      </div>
      <template #footer>
        <div class="modal-footer">
          <vs-button flat @click="showCancelConfirm = false" :disabled="isCancelling">ບໍ່</vs-button>
          <vs-button color="danger" @click="executeCancelJob" :loading="isCancelling">
            <i class="fal fa-check"></i> ຢືນຢັນ
          </vs-button>
        </div>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatErrorMessage, truncate, formatJson } from '@/services/walletFormatters';
import { STATS_POLLING_INTERVAL } from '@/services/walletConstants';

export default {
  name: 'JobDashboard',
  data() {
    return {
      // Stats
      stats: {},
      statsError: null,
      statsErrorRequestId: null,
      statsPollTimer: null,
      statsConsecutiveErrors: 0,
      serviceDown: false,

      // Jobs
      jobs: [],
      isLoadingJobs: false,
      jobsError: null,
      jobsErrorRequestId: null,
      jobAbortController: null,
      jobPagination: {
        page: 1,
        page_size: 20,
        total: 0,
      },
      filters: {
        queue: '',
        status: '',
        type: '',
      },

      // Detail
      showDetailModal: false,
      selectedJob: null,

      // Cancel
      showCancelConfirm: false,
      cancelTarget: null,
      isCancelling: false,
    };
  },
  computed: {
    queues() {
      const queueNames = Object.keys(this.stats).filter(k => k !== 'total');
      return [...queueNames, 'total'];
    },
    jobTotalPages() {
      return this.jobPagination.total_pages || Math.ceil(this.jobPagination.total / this.jobPagination.page_size) || 1;
    },
  },
  mounted() {
    this.fetchStats();
    this.fetchJobs();
    this.startStatsPoll();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  beforeDestroy() {
    this.stopStatsPoll();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.jobAbortController) {
      this.jobAbortController.abort();
    }
  },
  methods: {
    // Stats polling
    startStatsPoll() {
      this.statsPollTimer = setInterval(() => {
        if (!document.hidden) {
          this.fetchStats();
        }
      }, STATS_POLLING_INTERVAL);
    },

    stopStatsPoll() {
      if (this.statsPollTimer) {
        clearInterval(this.statsPollTimer);
        this.statsPollTimer = null;
      }
    },

    restartPollWithBackoff() {
      this.stopStatsPoll();
      // Back off: 30s after 3 errors, 60s after 5+
      const backoffInterval = this.statsConsecutiveErrors >= 5 ? 60000 : 30000;
      this.statsPollTimer = setInterval(() => {
        if (!document.hidden) {
          this.fetchStats();
        }
      }, backoffInterval);
    },

    onVisibilityChange() {
      if (!document.hidden) {
        this.fetchStats();
      }
    },

    async fetchStats() {
      this.statsError = null;
      this.statsErrorRequestId = null;
      try {
        const response = await walletService.getJobStats(this.$axios);
        this.stats = response.data || response || {};

        // Recovered from errors — reset backoff
        if (this.statsConsecutiveErrors > 0) {
          this.statsConsecutiveErrors = 0;
          this.serviceDown = false;
          this.stopStatsPoll();
          this.startStatsPoll();
        }

        // Compute total aggregation if not provided
        if (!this.stats.total) {
          const total = { pending: 0, active: 0, scheduled: 0, retry: 0, archived: 0 };
          for (const [key, val] of Object.entries(this.stats)) {
            if (key === 'total') continue;
            for (const status of Object.keys(total)) {
              total[status] += val[status] || 0;
            }
          }
          this.stats.total = total;
        }
      } catch (error) {
        this.statsConsecutiveErrors++;
        const { message, requestId } = extractErrorInfo(error);
        this.statsError = message || 'ບໍ່ສາມາດໂຫລດ stats ໄດ້';
        this.statsErrorRequestId = requestId;

        // After 3 consecutive failures, mark service as down and backoff polling
        if (this.statsConsecutiveErrors >= 3 && !this.serviceDown) {
          this.serviceDown = true;
          this.restartPollWithBackoff();
        }
      }
    },

    retryNow() {
      this.statsConsecutiveErrors = 0;
      this.serviceDown = false;
      this.stopStatsPoll();
      this.fetchStats();
      this.fetchJobs();
      this.startStatsPoll();
    },

    getStatValue(queue, status) {
      return this.stats[queue]?.[status] ?? '-';
    },

    // Jobs
    onFilterChange() {
      this.jobPagination.page = 1;
      this.fetchJobs();
    },

    async fetchJobs() {
      if (this.jobAbortController) {
        this.jobAbortController.abort();
      }
      this.jobAbortController = new AbortController();

      this.isLoadingJobs = true;
      this.jobsError = null;
      this.jobsErrorRequestId = null;

      try {
        const params = {
          page: this.jobPagination.page,
          page_size: this.jobPagination.page_size,
        };
        if (this.filters.queue) params.queue = this.filters.queue;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.type?.trim()) params.type = this.filters.type.trim();

        const response = await walletService.listJobs(
          this.$axios, params, this.jobAbortController.signal
        );
        this.jobs = response.data || [];
        this.jobPagination.total = response.meta?.total || 0;
        this.jobPagination.total_pages = response.meta?.total_pages || 0;
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        const { message, requestId } = extractErrorInfo(error);
        this.jobsError = message || 'ບໍ່ສາມາດໂຫລດ jobs ໄດ້';
        this.jobsErrorRequestId = requestId;
      } finally {
        this.isLoadingJobs = false;
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.jobTotalPages) return;
      this.jobPagination.page = page;
      this.fetchJobs();
    },

    async showJobDetail(job) {
      try {
        const response = await walletService.getJob(this.$axios, job.id);
        this.selectedJob = response.data || response;
        this.showDetailModal = true;
      } catch (error) {
        this.$notification?.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'ບໍ່ສາມາດໂຫລດ job ໄດ້'));
      }
    },

    // Cancel
    isCancellable(status) {
      return status === 'pending' || status === 'scheduled';
    },

    confirmCancelJob(job) {
      this.cancelTarget = job;
      this.showCancelConfirm = true;
    },

    async executeCancelJob() {
      if (!this.cancelTarget) return;

      this.isCancelling = true;
      try {
        await walletService.cancelJob(this.$axios, this.cancelTarget.id);
        this.$notification?.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
        this.showCancelConfirm = false;
        this.showDetailModal = false;
        this.cancelTarget = null;
        this.fetchJobs();
        this.fetchStats();
      } catch (error) {
        const { message, requestId } = extractErrorInfo(error);
        let displayMsg;
        if (error.response?.status === 409) {
          displayMsg = 'Job ກຳລັງດຳເນີນການຢູ່ ບໍ່ສາມາດຍົກເລີກໄດ້';
        } else {
          displayMsg = message || 'ບໍ່ສາມາດຍົກເລີກໄດ້';
        }
        if (requestId) displayMsg += ` (ref: ${requestId})`;
        this.$notification?.OpenNotification_Error('top-right', 'danger', 3000, displayMsg);
      } finally {
        this.isCancelling = false;
      }
    },

    // Formatting
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return dateStr;
    },

    truncate,
    formatJson,

    getQueueClass(queue) {
      const classes = {
        critical: 'is-danger',
        default: 'is-info',
        low: 'is-light',
      };
      return classes[queue] || 'is-light';
    },

    getJobStatusClass(status) {
      const classes = {
        pending: 'is-warning',
        active: 'is-info',
        scheduled: 'is-link',
        retry: 'is-danger is-light',
        archived: 'is-dark',
        completed: 'is-success',
      };
      return classes[status] || 'is-light';
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  margin-top: 20px;
}

.box-title {
  margin-bottom: 20px;

  h4 {
    font-size: 18px;
    font-weight: 600;

    i {
      margin-right: 8px;
      color: #17a2b8;
    }
  }
}

.service-down-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  i.fa-exclamation-triangle {
    font-size: 18px;
    color: #856404;
  }

  .ml-3 {
    margin-left: auto;
  }
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.p-4 {
  padding: 1rem;
}

.error-ref {
  font-size: 12px;
  color: #856404;
  margin-top: 4px;
}

// Stats grid
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  &__title {
    font-weight: 600;
    text-transform: capitalize;
  }

  &__badge {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
}

.badge-critical {
  background: #f8d7da;
  color: #721c24;
}

.badge-default {
  background: #cce5ff;
  color: #004085;
}

.badge-low {
  background: #e2e3e5;
  color: #383d41;
}

.badge-total {
  background: #d4edda;
  color: #155724;
}

.stat-item {
  display: flex;
  justify-content: space-between;
}

.stat-label {
  font-size: 13px;
  color: #666;
}

.stat-value {
  font-weight: 600;
  font-size: 14px;
}

.stat-active {
  color: #17a2b8;
}

.stat-retry {
  color: #dc3545;
}

.stat-archived {
  color: #6c757d;
}

// Job table
.table td {
  vertical-align: middle;
}

.job-type {
  font-size: 12px;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
}

.job-actions {
  display: flex;
  gap: 4px;
}

.pagination-wrapper {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pagination-info {
  color: #7a7a7a;
}

// Detail modal
.json-display {
  background: #f5f5f5;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  overflow-x: auto;
  max-height: 200px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.confirm-body {
  padding: 20px;
  text-align: center;
  font-size: 1rem;
}
</style>
